import type { MissingDoseInput, MissingDoseInputOption } from '@/types/products'

/**
 * Words for the dose inputs a split or pool could not find.
 *
 * The server decides what is missing (`inputsNotFound` on the split and pool
 * previews) and refuses the action over the same list; this only says it. Each
 * input is named by its label AND its Field Library system name, because the
 * fix is to place that library field on the form, or fill it in, and the system
 * name is how the form builder and the Field Library identify it.
 */

function option(o: MissingDoseInputOption): string {
  const why =
    o.reason === 'ambiguous'
      ? `on the form more than once with different values: ${(o.places ?? []).join(', ')}`
      : o.reason === 'no_value'
        ? 'on the form but empty'
        : 'not on the form'
  return `${o.label} (system name ${o.systemName}, ${why})`
}

/** One line per unmet requirement. */
export function inputsNotFoundLines(missing: readonly MissingDoseInput[] | undefined): string[] {
  return (missing ?? []).map(m => {
    const what = m.anyOf.length === 1 ? option(m.anyOf[0]) : `Either ${m.anyOf.map(option).join(' or ')}`
    const sentence = what.charAt(0).toUpperCase() + what.slice(1)
    return m.product ? `${m.product}: ${sentence}` : sentence
  })
}

/** The heading above those lines. */
export const INPUTS_NOT_FOUND_TITLE =
  'Cannot find the lab values on this form or any earlier form in the procedure'

/** What to do about it. */
export const INPUTS_NOT_FOUND_HINT =
  'Add the Field Library field with that system name to the form, or link a table cell to it under Cell types → Field Library field, and fill it in. Link each system name to one place per form. A field or cell with the same label but no Field Library link is not used.'

/**
 * Whether a Split or Pool can go ahead as far as its preview is concerned.
 *
 * The preview is the only place the operator sees the recalculated doses and
 * what is missing, so the action waits for it: while it loads, and when it
 * failed, the button stays off and this says why. A preview that loaded is
 * "ready" — whatever it reports is then judged by `inputsNotFound` and the
 * warnings, not here.
 */
export function previewGate(preview: {
  isSuccess: boolean
  isError: boolean
  error?: unknown
}): { ready: boolean; message: string | null } {
  if (preview.isSuccess) return { ready: true, message: null }
  if (preview.isError) {
    const reason = preview.error instanceof Error ? preview.error.message : 'unknown error'
    return { ready: false, message: `The LIMS recalculation could not be checked (${reason}). Try again before continuing.` }
  }
  return { ready: false, message: 'Checking the lab values…' }
}

/**
 * The Split dialog's notice for missing lab values (z8nrz7fcu4 follow-up).
 *
 * A split no longer stops over them: the operator has the lab report even when
 * the form was configured without a place for a value. So the notice offers
 * the two ways forward, typing the value or ordering the test, and says what
 * happens if they do neither.
 */
export const MISSING_LAB_VALUES_TITLE = 'Some lab values are not on the form yet'

export const MISSING_LAB_VALUES_HINT =
  'Enter the value from the lab report, or order the test. You can also split now: the figures that need a missing value are left blank, and the audit trail records what was entered by hand and what was missing.'

export interface MissingLabValueOption {
  systemName: string
  label: string
  /** Why the forms did not supply it, for a person. */
  why: string
}

/** One requirement: any ONE of its options satisfies it. */
export interface MissingLabValueRow {
  key: string
  options: MissingLabValueOption[]
}

function whyMissing(o: MissingDoseInputOption): string {
  if (o.reason === 'ambiguous') return `Two different values on the form (${(o.places ?? []).join(', ')})`
  if (o.reason === 'no_value') return 'On the form but not filled in'
  return 'No field for it on the form'
}

export function missingLabValueRows(missing: readonly MissingDoseInput[] | undefined): MissingLabValueRow[] {
  return (missing ?? []).map(m => ({
    key: m.anyOf.map(o => o.systemName).join('|'),
    options: m.anyOf.map(o => ({ systemName: o.systemName, label: o.label, why: whyMissing(o) })),
  }))
}

/**
 * What the operator typed, as the `manualInputs` the split and its preview
 * take. Blank and unusable entries are dropped (the server ignores them too);
 * undefined when nothing is left, so an untouched dialog sends what it always did.
 */
export function manualInputsPayload(draft: Record<string, string>): Record<string, number> | undefined {
  const out: Record<string, number> = {}
  for (const [key, raw] of Object.entries(draft)) {
    const text = raw.trim()
    if (text === '') continue
    const value = Number(text)
    if (Number.isFinite(value) && value >= 0) out[key] = value
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/**
 * The rows the dialog shows: every requirement seen since the product was
 * picked. A typed value removes its requirement from the server's answer, and
 * the input must not vanish under the operator's cursor when it does.
 */
export function mergeMissingRows(
  shown: MissingLabValueRow[],
  latest: MissingLabValueRow[],
): MissingLabValueRow[] {
  const fresh = latest.filter(r => !shown.some(s => s.key === r.key))
  return fresh.length === 0 ? shown : [...shown, ...fresh]
}
